import React, { useEffect, useMemo, useState } from "react";
import type { Machine } from "../services/BackendDataService";
import {
  peekMotorMonitoringService,
  type MotorState,
  type StreamInfoCallback,
} from "../services/MotorMonitoringService";
import { useMotorStates } from "../hooks/useMotorStates";
import SubTabNav from "./common/SubTabNav";
import ServoHistoricalValues from "./ServoHistoricalValues";
import { useTheme } from "../providers/ThemeProvider";
import TreeView, { type TreeNode, type TreeTone } from "./TreeView";
import ConnectionStatus from "../dashboard/panels/ConnectionStatus";
import WaitingState from "../dashboard/panels/WaitingState";
import SeriesChart, {
  type SeriesDef,
  type AxisDef,
} from "../dashboard/panels/SeriesChart";
import { DashboardSyncProvider } from "../dashboard/DashboardSyncContext";
import { formatMetricValue } from "../dashboard/format";
import type { SeriesPoint } from "../dashboard";

const cx = (...c: (string | false | null | undefined)[]) =>
  c.filter(Boolean).join(" ");

interface ServoConditionMonitoringProps {
  machineData?: Machine | null;
  /** ISO-8601 start time for historical replay; undefined = live only */
  startTime?: string;
  paused?: boolean;
  /** Called once with the stream's oldest/newest available timestamps. */
  onStreamInfo?: StreamInfoCallback;
  /**
   * Minimum ms between overview history points during replay.
   * Wider ranges pass a larger interval to keep point count manageable.
   */
  sampleIntervalMs?: number;
}

type Severity = "ok" | "warning" | "alarm";

interface AxisStatus {
  tone: TreeTone;
  label: string;
  severity: Severity;
}

/**
 * Classify a servo axis diagnostic message into a status.
 *   ""/"OK"/"Running"      → ok       (green)
 *   "Power is off"/"Idle"  → warning  (amber)
 *   anything else          → alarm    (red, e.g. "Stopped")
 */
function classifyStatus(diag?: string): AxisStatus {
  const msg = (diag ?? "").trim();
  if (!msg || /^(ok|running|normal|ready)$/i.test(msg))
    return { tone: "ok", label: msg || "OK", severity: "ok" };
  if (/power is off|switched off|\boff\b|idle|standby|warn/i.test(msg))
    return { tone: "warning", label: msg, severity: "warning" };
  return { tone: "alarm", label: msg, severity: "alarm" };
}

const SEVERITY_RANK: Record<Severity, number> = { ok: 0, warning: 1, alarm: 2 };

/** Group id = axis id without the trailing `_MAxx` token. */
const groupIdOf = (axisId: string): string => axisId.replace(/_MA\d+$/, "");

/**
 * Friendly group label: keep only the first and last `_`-segments, dropping
 * everything in between, so
 * `P1233_F_ZBSB_LO_14A_fbInnerFramePressingDevice` → `P1233_fbInnerFramePressingDevice`
 * and `P0655_F_ZFSB_fbCigFormation` → `P0655_fbCigFormation`.
 */
const groupLabel = (groupId: string): string => {
  const parts = groupId.split("_");
  if (parts.length <= 1) return groupId;
  return `${parts[0]}_${parts[parts.length - 1]}`;
};

/** Leaf label = the axis token (e.g. "MA03"), falling back to the full id. */
const axisLabel = (axisId: string): string =>
  axisId.match(/MA\d+/)?.[0] ?? axisId;

interface ServoMessage {
  axisId: string;
  status: AxisStatus;
}

// ─── Servo detail chart definitions (stable module-scope identities) ──────────
const TEMP_AXES: AxisDef[] = [
  { title: "°C", color: "#ef4444", opposite: false },
];
const TEMP_SERIES: SeriesDef[] = [
  {
    key: "tempMotor",
    name: "Temperature Motor",
    color: "#f97316",
    unit: "°C",
    axis: 0,
  },
  {
    key: "tempAmplifier",
    name: "Temperature Amplifier",
    color: "#ef4444",
    unit: "°C",
    axis: 0,
  },
];
const TORQUE_AXES: AxisDef[] = [
  { title: "Nm", color: "#22c55e", opposite: false },
];
const TORQUE_SERIES: SeriesDef[] = [
  {
    key: "torqueRms",
    name: "Torque RMS",
    color: "#22c55e",
    unit: "Nm",
    axis: 0,
    type: "areaspline",
  },
  { key: "torqueDc", name: "Torque DC", color: "#eab308", unit: "Nm", axis: 0 },
];

type ServoSubTab = "overview" | "historical" | "anomaly";
const SERVO_SUBTABS: { key: ServoSubTab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "historical", label: "Historical Values" },
  { key: "anomaly", label: "Anomaly Detection" },
];

const ServoConditionMonitoring: React.FC<ServoConditionMonitoringProps> = ({
  machineData,
  startTime,
  paused,
  onStreamInfo,
  sampleIntervalMs,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;

  const [subTab, setSubTab] = useState<ServoSubTab>("overview");
  const [selectedAxisId, setSelectedAxisId] = useState<string | null>(null);

  // Same options the Motor screen passes: `onStreamInfo` lets the parent learn
  // the stream's newest timestamp so preset ranges are computed relative to the
  // data (not wall-clock), and `sampleIntervalMs` keeps point counts sane.
  const { motors, isConnected, subscribed, replaying, machineBK } =
    useMotorStates(machineData, startTime, paused, {
      onStreamInfo,
      sampleIntervalMs,
    });

  // Select an axis → tell the service to load its history (Doris + live),
  // exactly like the motor detail view.
  const selectAxis = (axisId: string | null) => {
    setSelectedAxisId(axisId);
    if (machineBK)
      peekMotorMonitoringService(machineBK)?.setSelectedMotor(axisId);
  };

  // Switching live↔range recreates the monitoring service, which loses the
  // selected axis — re-assert it so the new (Doris) service fetches history
  // for the axis that's still on screen.
  useEffect(() => {
    if (!machineBK || !selectedAxisId || !subscribed) return;
    peekMotorMonitoringService(machineBK)?.setSelectedMotor(selectedAxisId);
  }, [machineBK, selectedAxisId, subscribed, startTime]);
  const handleDetailRequest = (startIso: string, endIso: string) => {
    if (machineBK)
      peekMotorMonitoringService(machineBK)?.loadRangeDetail(startIso, endIso);
  };
  const selectedAxis = selectedAxisId ? motors.get(selectedAxisId) : null;

  // In live mode, only show axes that have received data AFTER the initial
  // discovery pass. The worker resets lastUpdate to 0 at the end of discovery,
  // so only axes that subsequently receive live signals will have lastUpdate > 0.
  // In historical/range mode, show all discovered axes (Doris only returns
  // axes that have data in the requested window).
  const activeMotors = useMemo(() => {
    if (startTime) return motors; // range mode — show everything Doris found
    const filtered = new Map<string, MotorState>();
    for (const [id, motor] of motors) {
      if (motor.lastUpdate > 0) {
        filtered.set(id, motor);
      }
    }
    return filtered;
  }, [motors, startTime]);

  // Group axes by component (id without axis token) → axis leaves; collect
  // active (non-OK) messages. Group labels drop the `_F_<module>` token.
  const { treeNodes, alarms, warnings, axisCount } = useMemo(() => {
    const alarms: ServoMessage[] = [];
    const warnings: ServoMessage[] = [];
    const cmp = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true });

    const groups = new Map<string, MotorState[]>();
    for (const axis of activeMotors.values()) {
      const gid = groupIdOf(axis.id);
      const list = groups.get(gid);
      if (list) list.push(axis);
      else groups.set(gid, [axis]);
    }

    const nodes: TreeNode[] = Array.from(groups.entries())
      .sort((a, b) => cmp(a[0], b[0]))
      .map(([gid, axes]) => {
        let worst: Severity = "ok";
        const children: TreeNode[] = axes
          .slice()
          .sort((a, b) => cmp(a.id, b.id))
          .map((axis) => {
            const status = classifyStatus(axis.signals.diagMsg);
            if (SEVERITY_RANK[status.severity] > SEVERITY_RANK[worst])
              worst = status.severity;
            if (status.severity === "alarm")
              alarms.push({ axisId: axis.id, status });
            else if (status.severity === "warning")
              warnings.push({ axisId: axis.id, status });
            return {
              id: axis.id,
              label: axisLabel(axis.id),
              status: { tone: status.tone, label: status.label },
            } satisfies TreeNode;
          });

        return {
          id: gid,
          label: groupLabel(gid),
          status:
            worst === "ok"
              ? undefined
              : { tone: worst === "alarm" ? "alarm" : "warning" },
          children,
        } satisfies TreeNode;
      });

    return {
      treeNodes: nodes,
      alarms,
      warnings,
      axisCount: activeMotors.size,
    };
  }, [activeMotors]);

  // ── Not connected (live only — a date range loads from Doris regardless) ──
  if (!isConnected && !startTime) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
        <svg
          className={cx(
            "w-12 h-12",
            isDark ? "text-gray-600" : "text-gray-300",
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01M6.343 6.343a9 9 0 000 12.728"
          />
        </svg>
        <p
          className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}
        >
          NATS not connected
        </p>
      </div>
    );
  }

  // ── No machine ─────────────
  if (!machineBK) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p
          className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}
        >
          No machine selected
        </p>
      </div>
    );
  }

  const cardCls = cx(
    "rounded-xl border",
    isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
  );

  return (
    <div className="space-y-4">
      <SubTabNav
        tabs={SERVO_SUBTABS}
        activeTab={subTab}
        onTabChange={(k) => setSubTab(k as ServoSubTab)}
      />

      {subTab === "overview" && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <ConnectionStatus
              connected={axisCount > 0}
              label={
                axisCount > 0
                  ? `${axisCount} servo ${axisCount > 1 ? "axes" : "axis"} detected`
                  : subscribed
                    ? "Waiting for servo data…"
                    : "Connecting…"
              }
            />
            {replaying && (
              <span className="text-xs text-blue-500 animate-pulse">
                Replaying…
              </span>
            )}
          </div>

          {axisCount === 0 ? (
            <WaitingState
              title={
                startTime ? "Loading history…" : "Listening for servo signals…"
              }
              subject={
                startTime ? undefined : `edge-data.live.*.${machineBK}.>`
              }
              detail={
                startTime
                  ? "No servo axes have reported yet for this range."
                  : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
              {/* Tree */}
              <div className={cx(cardCls, "lg:col-span-2 p-4 flex flex-col")}>
                <h3
                  className={cx(
                    "text-sm font-bold mb-3",
                    isDark ? "text-gray-200" : "text-gray-700",
                  )}
                >
                  All axes
                </h3>
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                  <TreeView
                    nodes={treeNodes}
                    showControls
                    selectedId={selectedAxisId ?? undefined}
                    onSelect={(node) => {
                      if (!node.children || node.children.length === 0)
                        selectAxis(node.id);
                    }}
                  />
                </div>
              </div>

              {/* Current messages */}
              <div className={cx(cardCls, "lg:col-span-5 p-4")}>
                <h3
                  className={cx(
                    "text-sm font-bold mb-3",
                    isDark ? "text-gray-200" : "text-gray-700",
                  )}
                >
                  Current messages
                </h3>

                {alarms.length === 0 && warnings.length === 0 ? (
                  <div
                    className={cx(
                      "flex flex-col items-center justify-center py-14 text-center",
                      isDark ? "text-gray-500" : "text-gray-400",
                    )}
                  >
                    <span className="text-3xl mb-2">✅</span>
                    <p className="text-sm">All servo axes nominal</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {alarms.length > 0 && (
                      <MessageSection
                        title="Alarms"
                        tone="alarm"
                        messages={alarms}
                        isDark={isDark}
                        selectedId={selectedAxisId}
                        onSelect={selectAxis}
                      />
                    )}
                    {warnings.length > 0 && (
                      <MessageSection
                        title="Warnings"
                        tone="warning"
                        messages={warnings}
                        isDark={isDark}
                        selectedId={selectedAxisId}
                        onSelect={selectAxis}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedAxis && (
            <ServoDetail
              motor={selectedAxis}
              isDark={isDark}
              canDetail={!!startTime}
              onDetail={handleDetailRequest}
              onClose={() => selectAxis(null)}
            />
          )}
        </div>
      )}

      {subTab === "historical" && (
        <ServoHistoricalValues
          machineBK={machineBK}
          startTime={startTime}
          isDark={isDark}
        />
      )}
      {subTab === "anomaly" && (
        <ServoUnderConstruction
          isDark={isDark}
          title="Anomaly Detection"
          subtitle="Automated anomaly detection is coming soon."
        />
      )}
    </div>
  );
};

// ─── Placeholder for not-yet-built sub-tabs ───────────────────────────────────
const ServoUnderConstruction: React.FC<{
  isDark: boolean;
  title: string;
  subtitle: string;
}> = ({ isDark, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center min-h-[320px] text-center">
    <svg
      className={cx(
        "w-14 h-14 mb-4",
        isDark ? "text-gray-600" : "text-gray-300",
      )}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
    <h3
      className={cx(
        "text-lg font-semibold mb-1",
        isDark ? "text-white" : "text-gray-900",
      )}
    >
      {title}
    </h3>
    <p className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}>
      {subtitle}
    </p>
  </div>
);

// ─── Message section (Alarms / Warnings) ──────────────────────────────────────
const MessageSection: React.FC<{
  title: string;
  tone: "alarm" | "warning";
  messages: ServoMessage[];
  isDark: boolean;
  selectedId: string | null;
  onSelect: (axisId: string) => void;
}> = ({ title, tone, messages, isDark, selectedId, onSelect }) => {
  const accent =
    tone === "alarm"
      ? {
          dot: "bg-red-500",
          card: isDark
            ? "bg-red-900/15 border-red-800/50"
            : "bg-red-50 border-red-200",
          title: isDark ? "text-red-300" : "text-red-700",
          msg: isDark ? "text-red-300" : "text-red-700",
        }
      : {
          dot: "bg-amber-500",
          card: isDark
            ? "bg-amber-900/15 border-amber-800/50"
            : "bg-amber-50 border-amber-200",
          title: isDark ? "text-amber-300" : "text-amber-700",
          msg: isDark ? "text-amber-300" : "text-amber-700",
        };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={cx("w-2 h-2 rounded-full", accent.dot)} />
        <h4
          className={cx(
            "text-xs font-bold uppercase tracking-wide",
            accent.title,
          )}
        >
          {title}
        </h4>
        <span
          className={cx(
            "text-xs rounded-full px-1.5 py-0.5 font-semibold",
            isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600",
          )}
        >
          {messages.length}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {messages.map((m) => (
          <button
            key={m.axisId}
            type="button"
            onClick={() => onSelect(m.axisId)}
            className={cx(
              "relative rounded-xl border p-3 pr-9 text-left transition-all hover:shadow-md",
              accent.card,
              selectedId === m.axisId &&
                "ring-2 ring-blue-500 dark:ring-blue-400",
            )}
          >
            <div
              className={cx(
                "text-[0.65rem] font-mono truncate",
                isDark ? "text-gray-400" : "text-gray-500",
              )}
              title={m.axisId}
            >
              {m.axisId}
            </div>
            <div className={cx("text-sm font-bold mt-0.5", accent.msg)}>
              {m.status.label}
            </div>
            <span className="absolute top-2.5 right-2.5">
              {tone === "alarm" ? (
                <svg
                  className="w-5 h-5 text-red-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.7 7.3a1 1 0 00-1.4 1.4L8.6 10l-1.3 1.3a1 1 0 101.4 1.4L10 11.4l1.3 1.3a1 1 0 001.4-1.4L11.4 10l1.3-1.3a1 1 0 00-1.4-1.4L10 8.6 8.7 7.3z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-amber-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.492-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Servo detail (KPI chips + Temperature & Torque charts) ───────────────────
const ServoDetail: React.FC<{
  motor: MotorState;
  isDark: boolean;
  canDetail: boolean;
  onDetail: (startIso: string, endIso: string) => void;
  onClose: () => void;
}> = ({ motor, isDark, canDetail, onDetail, onClose }) => {
  const s = motor.signals;

  const historyLen = motor.history.length;
  const chartData = useMemo(() => {
    const keys = ["tempMotor", "tempAmplifier", "torqueRms", "torqueDc"];
    const map = new Map<string, SeriesPoint[]>();
    for (const k of keys) map.set(k, []);
    for (const point of motor.history) {
      for (const k of keys) {
        const v = (point as unknown as Record<string, unknown>)[k];
        if (typeof v === "number") map.get(k)!.push({ t: point.timestamp, v });
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor.id, historyLen]);

  const handleDetail = (startMs: number, endMs: number) =>
    onDetail(new Date(startMs).toISOString(), new Date(endMs).toISOString());

  const diagIsAlarm =
    !!s.diagMsg && !/^(ok|running|normal|ready)$/i.test(s.diagMsg.trim());

  const card = cx(
    "rounded-xl border",
    isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
  );
  const label = cx("text-xs mb-1", isDark ? "text-gray-400" : "text-gray-500");
  const value = cx(
    "text-lg font-bold",
    isDark ? "text-white" : "text-gray-900",
  );

  const chips: { label: string; value: React.ReactNode }[] = [
    {
      label: "Diagnosis Message",
      value: (
        <span
          className={
            diagIsAlarm
              ? "text-red-500"
              : isDark
                ? "text-white"
                : "text-gray-900"
          }
        >
          {s.diagMsg || "OK"}
        </span>
      ),
    },
    {
      label: "Temperature Motor",
      value: formatMetricValue(s.tempMotor, { decimals: 1, unit: "°C" }),
    },
    {
      label: "Temperature Amplifier",
      value: formatMetricValue(s.tempAmplifier, { decimals: 1, unit: "°C" }),
    },
    {
      label: "Torque RMS",
      value: formatMetricValue(s.torqueRms, { decimals: 2, unit: "Nm" }),
    },
    {
      label: "Torque DC",
      value: formatMetricValue(s.torqueDc, { decimals: 2, unit: "Nm" }),
    },
  ];

  return (
    <div className={cx(card, "p-4 sm:p-5 relative")}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close details"
        className={cx(
          "absolute top-3 right-3 p-1.5 rounded-lg transition-colors",
          isDark
            ? "text-gray-400 hover:bg-gray-700"
            : "text-gray-400 hover:bg-gray-100",
        )}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <h3
        className={cx(
          "text-base font-bold mb-3",
          isDark ? "text-gray-100" : "text-gray-800",
        )}
      >
        Servo Details
      </h3>

      {/* Identity */}
      <div
        className={cx(
          "text-sm space-y-0.5 mb-4",
          isDark ? "text-gray-300" : "text-gray-600",
        )}
      >
        <div>
          <span className="font-semibold">Motor:</span> {motor.id}
        </div>
        <div>
          <span className="font-semibold">Motor Type:</span> {s.motorTyp || "—"}
        </div>
        <div>
          <span className="font-semibold">Motor Part No.:</span>{" "}
          {s.motorPartNo || "—"}
        </div>
      </div>

      {/* KPI chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        {chips.map((c) => (
          <div key={c.label} className={cx(card, "p-3")}>
            <div className={label}>{c.label}</div>
            <div className={value}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <DashboardSyncProvider>
        <div className="space-y-4">
          <div className={cx(card, "p-3")}>
            <div className="h-64">
              <SeriesChart
                title="Temperature"
                seriesDefs={TEMP_SERIES}
                yAxes={TEMP_AXES}
                data={chartData}
                height={250}
                canDetail={canDetail}
                onDetail={handleDetail}
                commentType="servo_temperature"
                machineBK={motor.id}
              />
            </div>
          </div>
          <div className={cx(card, "p-3")}>
            <div className="h-64">
              <SeriesChart
                title="Torque"
                seriesDefs={TORQUE_SERIES}
                yAxes={TORQUE_AXES}
                data={chartData}
                height={250}
                canDetail={canDetail}
                onDetail={handleDetail}
                commentType="servo_torque"
                machineBK={motor.id}
              />
            </div>
          </div>
        </div>
      </DashboardSyncProvider>
    </div>
  );
};

export default ServoConditionMonitoring;
